/**
 * On-site location verification: distance thresholds and Haversine
 */

export const AUTO_ALLOW_RADIUS_M = 200;
export const WARN_RADIUS_M = 1000;

/** Accuracy at or below this (m) counts as "good signal" for verified confirm. */
export const LOCATION_GOOD_ACCURACY_M = 25;

/**
 * Whether GPS uncertainty is acceptable vs distance to property pin (pre-visit map UX).
 * - `goodSignal`: tight enough circle vs distance (or very good raw accuracy).
 * - `canConfirmVerified`: in range and good signal → primary "Confirm location".
 * - `canConfirmAnyway`: in range but not good signal → secondary override (logged as unverified-ish).
 */
export function locationSignalQuality(distanceM: number, accuracyM: number): {
  goodSignal: boolean;
  canConfirmVerified: boolean;
  canConfirmAnyway: boolean;
} {
  const inRange =
    Number.isFinite(distanceM) && distanceM >= 0 && distanceM <= AUTO_ALLOW_RADIUS_M;
  const acc = Number.isFinite(accuracyM) ? Math.max(0, accuracyM) : 9999;
  const dist = Number.isFinite(distanceM) ? Math.max(0, distanceM) : 9999;
  const goodSignal =
    acc <= LOCATION_GOOD_ACCURACY_M || acc <= Math.max(dist - 2, 12);
  const canConfirmVerified = inRange && goodSignal;
  const canConfirmAnyway = inRange && !goodSignal && acc < 800;
  return { goodSignal, canConfirmVerified, canConfirmAnyway };
}

/**
 * Haversine distance in meters between two lat/lng points
 */
export function haversineDistanceM(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000; // Earth radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function roundDistanceM(m: number): number {
  if (m < 100) return Math.round(m);
  if (m < 1000) return Math.round(m / 50) * 50;
  return Math.round(m / 100) * 100;
}

const M_PER_MI = 1609.344;

/**
 * Format distance for UI: meters when < 1 km, then miles (e.g. "500 m", "2.3 mi", "1,200 mi")
 * so the value is correct and readable (avoids showing raw "3393300 m").
 */
export function formatDistanceForDisplay(meters: number): string {
  if (meters < 0 || !Number.isFinite(meters)) return "—";
  if (meters < 1000) {
    const m = Math.round(meters);
    return `${m.toLocaleString()} m`;
  }
  const miles = meters / M_PER_MI;
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  if (miles < 100) return `${Math.round(miles * 10) / 10} mi`;
  return `${Math.round(miles).toLocaleString()} mi`;
}

/** True when `navigator.geolocation` is available (secure context except localhost). */
export function isGeolocationApiAvailable(): boolean {
  return typeof navigator !== "undefined" && !!navigator.geolocation;
}

export function isGeolocationPositionError(err: unknown): err is GeolocationPositionError {
  return typeof err === "object" && err !== null && "code" in err && typeof (err as GeolocationPositionError).code === "number";
}

/**
 * Try high-accuracy GPS first (better outdoors), then fall back to network/Wi‑Fi
 * positioning (often succeeds faster indoors). Reduces "stuck waiting" on mobile.
 */
export function getCurrentPositionWithFallback(): Promise<GeolocationPosition> {
  if (!isGeolocationApiAvailable()) {
    return Promise.reject(new Error("Geolocation not supported"));
  }

  const tryOnce = (options: PositionOptions) =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });

  return tryOnce({
    enableHighAccuracy: true,
    timeout: 20_000,
    maximumAge: 0,
  }).catch(() =>
    tryOnce({
      enableHighAccuracy: false,
      timeout: 20_000,
      maximumAge: 0,
    })
  );
}

/**
 * One quick fix for a location stamp. Never waits long: a recent fix (up to a minute old) is fine,
 * so a phone with no signal can still hand over the position it already knows.
 */
export function getStampPosition(): Promise<GeolocationPosition> {
  if (!isGeolocationApiAvailable()) {
    return Promise.reject(new Error("Geolocation not supported"));
  }
  const tryOnce = (options: PositionOptions) =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  return tryOnce({ enableHighAccuracy: true, timeout: 6_000, maximumAge: 15_000 }).catch((err) => {
    if (isGeolocationPositionError(err) && err.code === 1) throw err;
    return tryOnce({ enableHighAccuracy: false, timeout: 4_000, maximumAge: 60_000 });
  });
}
